/**
 * usePendingInspections — list cleaning_tasks awaiting supervisor approval.
 *
 * The cleaner has submitted (status = 'completed'); the supervisor is now
 * the gate to either `approve` (room → ready) or `reject` (task → rejected,
 * room → maintenance_required). RLS already restricts authenticated reads
 * to the supervisor's own organization, so the query needs no explicit
 * org filter — but we order by `completed_at` so the oldest submissions
 * surface first (a real ops queue, not chronological assignment order).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export interface PendingInspectionRow {
  id:           string
  room_id:      string
  status:       'completed'
  cycle_type:   string
  started_at:   string | null
  completed_at: string | null
  assigned_to_user_id: string | null
  // PROJECT_STATE task #73 — surfaced as a "Partial unit" chip on
  // each row so the supervisor can route it to the right approval
  // mindset without opening every task detail.
  is_partial_unit:           boolean
  kitchen_excluded:          boolean
  partial_scope_description: string | null
  // photo_coverage_status surfaces the AI-override path to the supervisor.
  // 'unavailable' here means the cleaner submitted while Anthropic was
  // down — they MUST manually verify the photos before approving. The
  // dashboard renders an "ReadyScan override — needs your eye" chip on these
  // rows (CLAUDE.md ERROR 16: supervisor approval is the documented
  // manual fallback).
  photo_coverage_status:     'pending' | 'covered' | 'incomplete' | 'unavailable' | null
  room: {
    id:             string
    room_number:    string
    current_status: string
    building_id:    string
    floor_id:       string
    campus_id:      string | null
    campus:   { id: string; name: string } | null
    building: { id: string; name: string } | null
    floor:    { id: string; label: string } | null
  } | null
  cleaner: {
    id:        string
    full_name: string | null
  } | null
}

export function usePendingInspections(
  campusId: string | null = null,
): UseQueryResult<PendingInspectionRow[]> {
  const supabase = useSupabase()

  return useQuery({
    // Cache by campus filter — narrowing should re-fetch instead of
    // serving the un-filtered cache from a moment ago.
    queryKey: ['pending-inspections', campusId ?? 'all'],
    queryFn: async (): Promise<PendingInspectionRow[]> => {
      const { data, error } = await supabase
        .from('cleaning_tasks')
        .select(
          'id, room_id, status, cycle_type, started_at, completed_at, assigned_to_user_id,' +
          ' is_partial_unit, kitchen_excluded, partial_scope_description, photo_coverage_status,' +
          ' room:rooms(id, room_number, current_status, building_id, floor_id, campus_id,' +
          '   campus:campuses(id, name),' +
          '   building:buildings(id, name),' +
          '   floor:floors(id, label)),' +
          ' cleaner:profiles!cleaning_tasks_assigned_to_user_id_fkey(id, full_name)',
        )
        .eq('status', 'completed')
        .order('completed_at', { ascending: true, nullsFirst: false })
        .limit(100)

      if (error) throw error
      const rows = (data ?? []) as unknown as PendingInspectionRow[]
      // Client-side campus filter — the result set is bounded at 100,
      // so this is cheap and avoids PostgREST's nested-filter quirks.
      return campusId
        ? rows.filter((r) => r.room?.campus_id === campusId)
        : rows
    },
    // Refresh every 30s so a supervisor sitting on the page sees new
    // submissions without manually reloading. The realtime subscription
    // hook is a separate concern (kept out of this PR's scope).
    refetchInterval: 30_000,
  })
}
